import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { useAuth } from "../lib/auth.js";
import { useDigestByDate } from "../lib/queries.js";
import { supabase } from "../lib/supabase.js";
import { AppShell } from "../components/AppShell.js";
import { Button, Card, FullPageSpinner } from "../components/ui.js";
import { DigestView } from "../components/DigestView.js";

function formatDate(iso: string): string {
  return new Date(iso + "T00:00:00").toLocaleDateString("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function DigestDetail() {
  const { user } = useAuth();
  const { date } = useParams<{ date: string }>();
  const { data: digest, isLoading } = useDigestByDate(user?.id, date);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  async function downloadPdf(path: string) {
    setPdfError(null);
    setPdfLoading(true);
    try {
      // Private bucket → kısa ömürlü signed URL (RLS: yalnızca kendi klasörü)
      const { data, error } = await supabase.storage.from("digests").createSignedUrl(path, 60);
      if (error || !data?.signedUrl) throw error ?? new Error("Bağlantı oluşturulamadı");
      window.open(data.signedUrl, "_blank", "noopener");
    } catch (e) {
      setPdfError("PDF açılamadı: " + ((e as Error).message ?? "bilinmeyen hata"));
    } finally {
      setPdfLoading(false);
    }
  }

  return (
    <AppShell>
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" /> Panele dön
      </Link>

      {isLoading ? (
        <FullPageSpinner label="Brifing yükleniyor…" />
      ) : !digest ? (
        <Card className="px-6 py-16 text-center">
          <h1 className="font-serif text-2xl text-slate-100">Brifing bulunamadı</h1>
          <p className="mt-2 text-sm text-slate-400">
            {date ? formatDate(date) : "Bu tarih"} için bir brifing kaydı yok.
          </p>
        </Card>
      ) : (
        <>
          <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow mb-2">Günlük Brifing</p>
              <h1 className="font-serif text-3xl text-slate-50">{formatDate(digest.digest_date)}</h1>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                loading={pdfLoading}
                disabled={!digest.pdf_url || pdfLoading}
                onClick={() => digest.pdf_url && downloadPdf(digest.pdf_url)}
                title={digest.pdf_url ? "PDF'i indir" : "Bu brifing için PDF üretilmedi"}
              >
                <Download className="h-4 w-4" /> PDF indir
                {!digest.pdf_url && <span className="ml-1 text-xs text-slate-500">(yok)</span>}
              </Button>
              {pdfError && <span className="text-xs text-red-400">{pdfError}</span>}
            </div>
          </header>

          <Card className="px-6 py-7 sm:px-8">
            <DigestView markdown={digest.content_md} />
          </Card>
        </>
      )}
    </AppShell>
  );
}
